import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * Parses a stat value string like "300", "2,000", "100,000", "1.7B", "300+"
 * into { number, suffix, decimals, hasCommas } for animation.
 */
function parseValue(v) {
    const s = String(v ?? "").trim();
    const m = s.match(/^([\d,.]+)(.*)$/);
    if (!m) return { num: 0, suffix: s, decimals: 0, hasCommas: false };
    const numStr = m[1];
    const suffix = m[2] || "";
    const hasCommas = numStr.includes(",");
    const clean = numStr.replace(/,/g, "");
    const decimals = clean.includes(".") ? Math.min(2, clean.split(".")[1].length) : 0;
    const num = parseFloat(clean) || 0;
    return { num, suffix, decimals, hasCommas };
}

function format(value, decimals, hasCommas) {
    const fixed = decimals > 0 ? value.toFixed(decimals) : Math.round(value).toString();
    if (!hasCommas) return fixed;
    const [intPart, fracPart] = fixed.split(".");
    const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return fracPart ? `${withCommas}.${fracPart}` : withCommas;
}

export default function CountUp({ value, duration = 2000, className = "" }) {
    const { num, suffix, decimals, hasCommas } = useMemo(() => parseValue(value), [value]);
    const [display, setDisplay] = useState(0);
    const ref = useRef(null);
    const started = useRef(false);

    useEffect(() => {
        started.current = false;
        setDisplay(0);
        if (!ref.current) return;
        const node = ref.current;
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting && !started.current) {
                        started.current = true;
                        const start = performance.now();
                        const tick = (now) => {
                            const t = Math.min(1, (now - start) / duration);
                            const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
                            setDisplay(num * eased);
                            if (t < 1) requestAnimationFrame(tick);
                            else setDisplay(num);
                        };
                        requestAnimationFrame(tick);
                    }
                });
            },
            { threshold: 0.35 }
        );
        obs.observe(node);
        return () => obs.disconnect();
    }, [num, duration]);

    return (
        <span ref={ref} className={className} data-testid="count-up">
            {format(display, decimals, hasCommas)}
            {suffix}
        </span>
    );
}
